import { backgroundTheme } from '~/modules/elements/layout-with-background';
import { ClientSubscriptionManager } from '~/modules/network/rpc/subscription-manager';
import { PlayerNumber } from '~/modules/players/player-number';
import { Grade } from '~/modules/user-profiles/profile-stats';
import { HelpEntry } from '~/routes/keyboard-help/context';

/**
 * A playlist as mirrored to the remote mic. The host's `display` is JSX (flags, logos, tooltips),
 * none of which crosses the wire — so a plain `label` is sent instead (`remoteLabel ?? name`).
 */
export interface RemotePlaylistEntry {
  name: string;
  label: string;
  count?: number;
}

/**
 * A song group as mirrored to the remote mic, carrying the same two highlight states the on-screen
 * group nav row shows, so the phone can render them identically without recomputing anything.
 */
export interface RemoteSongGroupEntry {
  name: string;
  label: string;
  /** The group's header is currently scrolled into view in the song list — full highlight. */
  visible: boolean;
  /** The currently selected song lives in this group — subtle highlight, unless already `visible`. */
  containsSelectedSong: boolean;
}

/** Everything the phone needs to mirror the song selection screen's toolbar and group nav row. */
export interface RemoteSongSelectionState {
  playlists: RemotePlaylistEntry[];
  selectedPlaylist: string | null;
  groups: RemoteSongGroupEntry[];
}

/**
 * A singer the game already knows, as offered to a phone joining. The phone has never been told who
 * exists - it stores one name locally and the game matches scores by that string - so a person who
 * has sung here before had to type their name again, exactly, or start a second history under a
 * spelling that differs by a capital.
 *
 * `heldByMicId` is what makes one singer one phone: a second phone claiming the same name would
 * split a round's scores between two entries of the same person.
 */
export interface RemoteSingerEntry {
  name: string;
  plays: number;
  /** Best grade across every recorded play; `null` for a profile that has not sung yet. */
  bestGrade: Grade | null;
  heldByMicId: string | null;
}

/**
 * The song on the TV right now, or `undefined` between songs. The phone shows its own singing screen
 * while one runs and locks the other two tabs, and neither is answerable from the mirrored keyboard
 * alone: plain singing registers a keyboard with no title, which is indistinguishable from any other
 * screen that happens to register none.
 *
 * Deliberately no position. The singing screen was designed without a clock so nothing has to be
 * pushed on every frame; this is published twice per song, at its start and at its end.
 */
export interface RemoteCurrentSong {
  title: string;
  artist: string;
  /**
   * False while the game is still waiting for every microphone to confirm somebody is holding it,
   * true once the song is actually running. The phone's Ready prompt needs both edges: it opens on
   * the request and has to take itself off screen when the song starts, whether or not this phone
   * ever answered - a prompt that only closes on its own button stays up through the whole song for
   * anybody who never tapped it.
   */
  started: boolean;
}

export interface RemoteQueueEntry {
  id: string;
  title: string;
  artist: string;
  video?: string;
  duration?: number;
  // The name of the phone that queued it; absent when it was queued on the television.
  addedBy?: string;
  addedById?: string;
}

/** Typed subscription channels and the data type pushed for each channel.
 * A type alias (not interface) so it satisfies the RPC core's AnySubscriptionChannels constraint. */
export type SubscriptionChannels = {
  'remote-mics': Array<{ id: string; name: string; number: PlayerNumber | null }>;
  'keyboard-layout': HelpEntry | undefined;
  // Published only while the song selection screen is mounted; `undefined` once it unmounts, so a
  // phone that reconnects onto another screen never renders a stale playlist/group row.
  'song-selection': RemoteSongSelectionState | undefined;
  /**
   * The ids of songs this machine has a play recorded for. The phone's Played filter has to ask the
   * question of the TV's own history - a phone knows nothing about what the room has sung - and it
   * is the only one of the song filters that is not an attribute of the song.
   */
  'played-songs': string[];
  singers: RemoteSingerEntry[];
  'current-song': RemoteCurrentSong | undefined;
  'song-queue': RemoteQueueEntry[];
  style: backgroundTheme;
};

export type ChannelName = keyof SubscriptionChannels;
export type ChannelData<C extends ChannelName> = SubscriptionChannels[C];

/** Singleton used by remote-mic hooks and NetworkClient. */
export const subscriptionManager = new ClientSubscriptionManager<SubscriptionChannels>();
